import { RootState } from '@store/store';
import { auth } from '@config/firebase';

// Constants for localStorage keys
export const STATE_PERSISTENCE_KEYS = {
  REDUX_STATE: 'factori_redux_state',
  PAYMENT_SESSION_DATA: 'factori_payment_session_data',
} as const;

// Interface for payment session data
export interface PaymentSessionData {
  sessionId: string;
  timestamp: number;
  returnUrl: string;
}

/**
 * Saves Redux state to localStorage
 * @param state - The Redux state to persist
 */
export const saveReduxState = (state: RootState): void => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(STATE_PERSISTENCE_KEYS.REDUX_STATE, serializedState);
  } catch (error) {
    console.error('Failed to save Redux state:', error);
  }
};

/**
 * Loads Redux state from localStorage
 * @returns The persisted Redux state or null if not found/invalid
 */
export const loadReduxState = (): Partial<RootState> | null => {
  try {
    const serializedState = localStorage.getItem(STATE_PERSISTENCE_KEYS.REDUX_STATE);
    if (!serializedState) {
      return null;
    }
    return JSON.parse(serializedState);
  } catch (error) {
    console.error('Failed to load Redux state:', error);
    return null;
  }
};

/**
 * Clears persisted Redux state from localStorage
 */
export const clearReduxState = (): void => {
  try {
    localStorage.removeItem(STATE_PERSISTENCE_KEYS.REDUX_STATE);
  } catch (error) {
    console.error('Failed to clear Redux state:', error);
  }
};

/**
 * Saves payment session data to localStorage
 * @param sessionData - The payment session data to persist
 */
export const savePaymentSessionData = (sessionData: PaymentSessionData): void => {
  try {
    const serializedData = JSON.stringify(sessionData);
    localStorage.setItem(STATE_PERSISTENCE_KEYS.PAYMENT_SESSION_DATA, serializedData);
  } catch (error) {
    console.error('Failed to save payment session data:', error);
  }
};

/**
 * Loads payment session data from localStorage
 * @returns The persisted payment session data or null if not found/invalid
 */
export const loadPaymentSessionData = (): PaymentSessionData | null => {
  try {
    const serializedData = localStorage.getItem(STATE_PERSISTENCE_KEYS.PAYMENT_SESSION_DATA);
    if (!serializedData) {
      return null;
    }

    const sessionData = JSON.parse(serializedData);
    return sessionData;
  } catch (error) {
    console.error('Failed to load payment session data:', error);
    return null;
  }
};

/**
 * Clears persisted payment session data from localStorage
 */
export const clearPaymentSessionData = (): void => {
  try {
    localStorage.removeItem(STATE_PERSISTENCE_KEYS.PAYMENT_SESSION_DATA);
  } catch (error) {
    console.error('Failed to clear payment session data:', error);
  }
};

/**
 * Clears all factori-related localStorage keys
 * This includes current keys and any legacy keys from previous implementations
 */
export const clearAllFactoriLocalStorage = (): void => {
  try {
    // Current implementation keys
    localStorage.removeItem('factori_redux_state');
    localStorage.removeItem('factori_payment_session_data');
    localStorage.removeItem('factori_state_restoration_in_progress');

    // Legacy keys that might exist from previous implementations
    localStorage.removeItem('factori_bearer_token_timestamp');
    localStorage.removeItem('factori_redux_state_timestamp');

    // Clear any other factori-related keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('factori_')) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    console.log(`Cleared ${keysToRemove.length} factori-related localStorage keys`);
  } catch (error) {
    console.error('Failed to clear factori localStorage keys:', error);
  }
};

/**
 * Clears all persisted data from localStorage
 */
export const clearAllPersistedData = (): void => {
  clearAllFactoriLocalStorage();
};

/**
 * Checks if the current page load is after a redirect from Stripe
 * @returns true if this is a post-redirect load
 */
export const isPostRedirectLoad = (): boolean => {
  const sessionData = loadPaymentSessionData();
  if (!sessionData) {
    return false;
  }

  // Check if we have session data and we're on a payment result page
  const currentPath = window.location.pathname;
  const isPaymentResultPage =
    currentPath.includes('/payment/success') || currentPath.includes('/payment/failure');

  return isPaymentResultPage;
};

/**
 * Gets the time elapsed since the payment session was created
 * @returns Time in milliseconds, or null if no session data
 */
export const getPaymentSessionAge = (): number | null => {
  const sessionData = loadPaymentSessionData();
  if (!sessionData) {
    return null;
  }

  return Date.now() - sessionData.timestamp;
};

/**
 * Checks if the payment session data is still valid (not expired)
 * @param maxAgeMs - Maximum age in milliseconds (default: 30 minutes)
 * @returns true if session data is valid
 */
export const isPaymentSessionValid = (maxAgeMs: number = 30 * 60 * 1000): boolean => {
  const age = getPaymentSessionAge();
  if (age === null) {
    return false;
  }

  return age <= maxAgeMs;
};

/**
 * Sets a flag indicating that state restoration is in progress
 */
export const setStateRestorationInProgress = (): void => {
  try {
    localStorage.setItem('factori_state_restoration_in_progress', 'true');
  } catch (error) {
    console.error('Failed to set state restoration flag:', error);
  }
};

/**
 * Clears the state restoration flag
 */
export const clearStateRestorationFlag = (): void => {
  try {
    localStorage.removeItem('factori_state_restoration_in_progress');
  } catch (error) {
    console.error('Failed to clear state restoration flag:', error);
  }
};

/**
 * Checks if state restoration is in progress
 */
export const isStateRestorationInProgress = (): boolean => {
  try {
    return localStorage.getItem('factori_state_restoration_in_progress') === 'true';
  } catch (error) {
    console.error('Failed to check state restoration flag:', error);
    return false;
  }
};

/**
 * Waits for Firebase auth state to be restored
 * @param maxWaitTime - Maximum time to wait in milliseconds (default: 5000)
 * @returns Promise that resolves to true if user is authenticated, false otherwise
 */
export const waitForFirebaseAuth = (maxWaitTime = 5000): Promise<boolean> => {
  return new Promise(resolve => {
    if (auth.currentUser) {
      resolve(true);
      return;
    }

    const unsubscribe = auth.onAuthStateChanged(user => {
      unsubscribe();
      resolve(!!user);
    });

    // Timeout after maxWaitTime
    setTimeout(() => {
      unsubscribe();
      resolve(false);
    }, maxWaitTime);
  });
};
